import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams, Link } from 'react-router-dom';
import { paymentBankApi, type BankEditBody } from '../../api/paymentBank.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { BankSelect } from '../../components/BankSelect.js';
import { reportsApi } from '../../api/reports.js';
import { ApiError } from '../../api/client.js';
import { Spinner, ErrorBox, EmptyBox, Pagination, SearchInput, Field } from '../../components/ui.js';
import { text, money, dateOnly, formatAmountDraft, isZeroDate, localDate } from '../../lib/format.js';
import { useUrlUpdate, useUrlSearch } from '../../lib/hooks.js';

/**
 * 업체 입금 관리 — 원본 payment/bank_list.php 를 참고해 다시 만든 화면.
 *
 * 목록 조회는 그대로 정의 기반 화면(/api/reports/payment-bank)을 쓴다. 원본이
 * is_filter 드롭다운으로 걸던 처리상태는 탭 버튼으로, bank_modify.php 가
 * iframe 모달로 열던 계산서/보고서/입금완료 확인은 표 안 버튼 하나로 바꿨다.
 * bank_check.php · bank_income_check.php · bank_excel_check.php 세 엑셀 검증
 * 도구도 각자 전용 화면(/payment/bank/check 등)으로 옮겨 붙였다.
 */

const CDN_BANK_ICON = 'https://cdn-banana.bizhost.kr/files/bank/';

const STATE_TABS = [
  { value: '0', label: '미완료 입금건' },
  { value: '1', label: '입금완료 · 발행대기' },
  { value: '2', label: '완료건' },
  { value: '3', label: '대기건' },
];

// label 은 수정 모달의 라디오 버튼 문구(설명이 필요해서 길다), shortLabel 은
// 목록 표 배지용 — 표에서 "선입금(발행대기)" 처럼 긴 문구가 계산서 상태 칸을
// 늘려버려서 짧게 줄이고, 나머지는 title 로 보여준다.
const TAX_BADGE: Record<string, { label: string; shortLabel: string; className: string }> = {
  '0': { label: '미발행', shortLabel: '미발행', className: 'badge badge--warn' },
  '1': { label: '발행필요', shortLabel: '발행필요', className: 'badge' },
  '2': { label: '선입금(발행대기)', shortLabel: '선입금', className: 'badge badge--danger' },
  '3': { label: '보류건(대기)', shortLabel: '보류건', className: 'badge' },
};

type Row = Record<string, unknown>;

/**
 * 처리상태 버튼 하나. 안 찍힌 상태면 "확인" 버튼만 있다가, 누르면 날짜(+확인자)
 * 입력이 펼쳐진다 — 원본이 iframe 모달로 열던 것을 표 안에서 바로 하게 만든 것.
 */
function StatusButton({
  seq,
  field,
  withName,
  currentName,
  onSaved,
}: {
  seq: number;
  field: 'tax_date' | 'report_date' | 'succ_date';
  withName: boolean;
  currentName?: string;
  onSaved: () => void;
}) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(localDate());
  const [name, setName] = useState(currentName ?? '');

  const mutation = useMutation({
    mutationFn: () => paymentBankApi.setStatus(seq, { field, date, name: withName ? name : undefined }),
    onSuccess: () => {
      setOpen(false);
      onSaved();
    },
  });

  // 이 버튼(행)은 목록과 함께 곧바로 마운트된다 - useState 초깃값 시점엔
  // useAuth() 의 계정 정보가 아직 안 왔을 수 있다("+ 입금건 추가" 모달처럼
  // 클릭해서 나중에 여는 게 아니라서). 그래서 실제로 여는 시점(클릭)에 채운다 -
  // 이미 확인자가 찍힌 건(수정)은 그대로 두고, 아직 없는 건(첫 확인)만
  // 로그인한 계정 이름을 기본값으로 채운다 - 등록자(up_name)와 같은 규칙.
  function openEditor() {
    setOpen(true);
    setName((prev) => prev || user?.name || user?.userid || '');
  }

  if (!open) {
    return (
      <button type="button" className="btn btn--ghost btn--sm" onClick={openEditor}>
        확인
      </button>
    );
  }

  return (
    <div className="status-editor">
      {withName && (
        <input
          className="input input--sm"
          placeholder="확인자"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      )}
      <div className="status-editor__row">
        <input
          className="input input--sm"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => setDate(localDate())}>
          오늘
        </button>
      </div>
      {mutation.isError && (
        <div className="qty-msg qty-msg--error">
          {mutation.error instanceof ApiError ? mutation.error.message : '저장에 실패했습니다.'}
        </div>
      )}
      <div className="status-editor__row">
        <button
          type="button"
          className="btn btn--primary btn--sm"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          저장
        </button>
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => setOpen(false)}>
          취소
        </button>
      </div>
    </div>
  );
}

interface FormState {
  corp: string;
  bankcode: string;
  banksn: string;
  bankinname: string;
  bankmemo: string;
  amountDraft: string;
  no_tax: string;
  up_name: string;
  tax_name: string;
  tax_date: string;
  re_name: string;
  report_date: string;
  succ_date: string;
  memo: string;
}

/**
 * '복사'는 원본 bank_add.php 의 seq 복사 동작과 같다 - 등록 화면(추가)을 그대로
 * 열되, 은행·계좌번호·예금주·입금액·계산서 발행 여부·입금 사유·비고만 원본 건에서
 * 옮겨 채운다. 등록자는(원본이 쿠키의 "마지막에 쓴 이름"을 쓰듯) 복사해 온 건의
 * 등록자가 아니라 지금 로그인한 계정 이름을 그대로 쓰고, 계산서/보고서/입금완료
 * 확인 값은 새 건이니 전부 빈 채로 시작한다 - 그래서 '수정'과 달리 row 를
 * 그대로 다 베끼지 않는다.
 */
function toFormState(row: Row | null, corp: string, defaultUpName: string, mode: 'create' | 'edit' | 'copy'): FormState {
  const isEdit = mode === 'edit';
  // 추가·복사에 걸쳐 공통으로 베끼는 값 - 복사가 아니면(추가) row 가 애초에 null.
  const src = isEdit || mode === 'copy' ? row : null;
  return {
    corp: src ? String(src.corp ?? corp) : corp,
    bankcode: src ? String(src.bankcode ?? '') : '',
    banksn: src ? String(src.banksn ?? '') : '',
    bankinname: src ? String(src.bankinname ?? '') : '',
    bankmemo: src ? String(src.bankmemo ?? '') : '',
    amountDraft: src?.amount ? Number(src.amount).toLocaleString() : '',
    no_tax: src ? String(src.no_tax ?? '1') : '1',
    memo: src ? String(src.memo ?? '') : '',
    // 새로 등록할 땐(복사 포함) 로그인한 계정 이름을 기본값으로 채운다 (다른
    // 사람 대신 등록하는 경우도 있어 수정은 계속 허용한다).
    up_name: isEdit && row ? String(row.up_name ?? '') : defaultUpName,
    // 계산서/보고서/입금완료 확인은 그 건 고유의 처리 이력이다 - 복사는 새
    // 건이니 여기서부터 다시 시작한다(원본과 같다).
    tax_name: isEdit && row ? String(row.tax_name ?? '') : '',
    tax_date: isEdit && row && !isZeroDate(row.tax_date) ? String(row.tax_date).slice(0, 10) : '',
    re_name: isEdit && row ? String(row.re_name ?? '') : '',
    report_date: isEdit && row && !isZeroDate(row.report_date) ? String(row.report_date).slice(0, 10) : '',
    succ_date: isEdit && row && !isZeroDate(row.succ_date) ? String(row.succ_date).slice(0, 10) : '',
  };
}

/** 등록/수정/복사 모달. 원본 bank_add.php · bank_modify.php 의 폼 화면을 합쳤다. */
function BankFormModal({
  mode,
  initial,
  corp,
  onClose,
  onSaved,
}: {
  mode: 'create' | 'edit' | 'copy';
  initial: Row | null;
  corp: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { user } = useAuth();
  const [form, setForm] = useState<FormState>(() =>
    toFormState(initial, corp, user?.name ?? user?.userid ?? '', mode),
  );

  const codes = useQuery({
    queryKey: ['payment-bank', 'codes'],
    queryFn: () => paymentBankApi.codes(),
    staleTime: 10 * 60 * 1000,
  });

  const seq = mode === 'edit' && initial ? Number(initial.seq) : 0;

  const body: BankEditBody = {
    bankcode: form.bankcode,
    banksn: form.banksn,
    bankinname: form.bankinname,
    bankmemo: form.bankmemo,
    amount: form.amountDraft,
    no_tax: Number(form.no_tax),
    up_name: form.up_name,
    tax_name: form.tax_name,
    tax_date: form.tax_date,
    re_name: form.re_name,
    report_date: form.report_date,
    succ_date: form.succ_date,
    memo: form.memo,
  };

  const save = useMutation({
    mutationFn: () =>
      mode === 'edit'
        ? paymentBankApi.update(seq, body)
        : paymentBankApi.create({ ...body, corp: form.corp }),
    onSuccess: onSaved,
  });

  const valid = form.bankcode && form.up_name && form.amountDraft;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-close" onClick={onClose} aria-label="닫기">
          ×
        </button>
        <h2 className="modal-title">
          <span style={{ color: 'var(--accent)' }}>🏦</span>
          {mode === 'create' ? '입금건 추가' : mode === 'copy' ? '입금건 복사' : '입금건 수정'}
        </h2>

        {mode === 'edit' && initial && (
          // 예금주·입금액·계좌번호·입금 사유는 바로 아래 편집 필드에도 그대로
          // 나온다 - 여기 요약에 또 적으면 같은 값이 두 번 보인다("중복으로
          // 표시" 리포트). 아래 필드에 없는, 이 건을 식별하는 값만 남긴다.
          <div className="form-summary">
            <div className="form-summary__corp">관리 법인: {String(initial.corp ?? corp)}</div>
            <div className="form-summary__sub">
              접수번호 #{String(initial.seq ?? '')} · 등록일: {dateOnly(initial.regdate as string)}
            </div>
          </div>
        )}

        <form
          // form-grid(auto-fit 다열 그리드) 는 이 폼처럼 자식이 전부 폭 전체를
          // 차지하는 형태(.row-2 두 줄 + 단독 필드 여럿)일 때, 일부 브라우저가
          // 그 배치를 DOM 순서와 다르게(심하면 같은 줄에 겹쳐서) 그리는 버그가
          // 있다(styles.css 의 .form-grid--stack 주석 - 실제 재현 사례가
          // 있었다) - 여기서도 그 증상(예금주/입금액 행이 은행선택/계좌번호
          // 행과 겹쳐 보임, "중복 표시" 리포트)이 재현돼 flex 로 바꿨다. flex
          // 는 자리 계산 여지가 없어 항상 DOM 순서대로 쌓인다 - 안쪽 .row-2 는
          // 자기가 grid 라 그대로 나란히 놓인다.
          className="form-stack"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <div className="row-2">
            <Field label="은행 선택" as="div">
              <BankSelect
                value={form.bankcode}
                options={codes.data ?? []}
                onChange={(code) => setForm((f) => ({ ...f, bankcode: code }))}
              />
            </Field>
            <Field label="계좌번호">
              <input
                className="input"
                value={form.banksn}
                onChange={(e) => setForm((f) => ({ ...f, banksn: e.target.value }))}
              />
            </Field>
          </div>

          <div className="row-2">
            <Field label="예금주">
              <input
                className="input"
                value={form.bankinname}
                onChange={(e) => setForm((f) => ({ ...f, bankinname: e.target.value }))}
              />
            </Field>
            <Field label="입금액">
              <input
                className="input input--amount"
                value={form.amountDraft}
                onChange={(e) => setForm((f) => ({ ...f, amountDraft: formatAmountDraft(e.target.value) }))}
              />
            </Field>
          </div>

          <Field label="입금 사유">
            <input
              className="input"
              value={form.bankmemo}
              onChange={(e) => setForm((f) => ({ ...f, bankmemo: e.target.value }))}
            />
          </Field>

          <Field label="계산서 발행 여부">
            <div className="radio-group">
              {Object.entries(TAX_BADGE).map(([val, meta]) => (
                <label key={val} className="radio-label">
                  <input
                    type="radio"
                    name="no_tax"
                    checked={form.no_tax === val}
                    onChange={() => setForm((f) => ({ ...f, no_tax: val }))}
                  />
                  {meta.label}
                </label>
              ))}
            </div>
          </Field>

          <Field label="등록자">
            <input
              className="input"
              value={form.up_name}
              onChange={(e) => setForm((f) => ({ ...f, up_name: e.target.value }))}
            />
          </Field>

          {mode === 'edit' && (
            <>
              <div className="form-divider">계산서 발행 확인</div>
              <div className="row-2">
                <Field label="계산서 발행 확인자">
                  <input
                    className="input"
                    value={form.tax_name}
                    onChange={(e) => setForm((f) => ({ ...f, tax_name: e.target.value }))}
                  />
                </Field>
                <Field label="계산서 발행 확인일">
                  <div className="status-editor__row">
                    <input
                      className="input"
                      type="date"
                      value={form.tax_date}
                      onChange={(e) => setForm((f) => ({ ...f, tax_date: e.target.value }))}
                    />
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => setForm((f) => ({ ...f, tax_date: localDate() }))}
                    >
                      오늘
                    </button>
                  </div>
                </Field>
              </div>

              <div className="form-divider">보고건 등록</div>
              <div className="row-2">
                <Field label="보고건 등록자">
                  <input
                    className="input"
                    value={form.re_name}
                    onChange={(e) => setForm((f) => ({ ...f, re_name: e.target.value }))}
                  />
                </Field>
                <Field label="보고건 등록일">
                  <div className="status-editor__row">
                    <input
                      className="input"
                      type="date"
                      value={form.report_date}
                      onChange={(e) => setForm((f) => ({ ...f, report_date: e.target.value }))}
                    />
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => setForm((f) => ({ ...f, report_date: localDate() }))}
                    >
                      오늘
                    </button>
                  </div>
                </Field>
              </div>

              <div className="form-divider">입금 완료 처리</div>
              <Field label="입금 완료일">
                <div className="status-editor__row">
                  <input
                    className="input"
                    type="date"
                    value={form.succ_date}
                    onChange={(e) => setForm((f) => ({ ...f, succ_date: e.target.value }))}
                  />
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => setForm((f) => ({ ...f, succ_date: localDate() }))}
                  >
                    오늘
                  </button>
                </div>
              </Field>
            </>
          )}

          <div className="form-divider">비고</div>
          <Field label="비고 (메모)">
            <textarea
              className="input input--area"
              value={form.memo}
              onChange={(e) => setForm((f) => ({ ...f, memo: e.target.value }))}
            />
          </Field>

          {save.isError && (
            <div className="qty-msg qty-msg--error">
              {save.error instanceof ApiError ? save.error.message : '저장에 실패했습니다.'}
            </div>
          )}

          <button type="submit" className="btn btn--primary btn--block" disabled={!valid || save.isPending}>
            {save.isPending ? '저장 중…' : '저장하기'}
          </button>
        </form>
      </div>
    </div>
  );
}

export function BankPage() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const update = useUrlUpdate(params, setParams);
  const [q, setQ] = useUrlSearch(params, update, 'q');

  const corp = params.get('corp') || 'bnncompany';
  const state = params.get('state') || '0';
  const page = Number(params.get('page') || '1');

  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [confirmingDelete, setConfirmingDelete] = useState<number | null>(null);
  const [formModal, setFormModal] = useState<{ mode: 'create' | 'edit' | 'copy'; row: Row | null } | null>(null);

  const meta = useQuery({
    queryKey: ['report', 'payment-bank', 'meta'],
    queryFn: () => reportsApi.meta('payment-bank'),
    staleTime: 5 * 60 * 1000,
  });

  const listParams = useMemo(
    () => ({ q, corp, state, page, perPage: 100, sort: 'regdate', dir: 'DESC' }),
    [q, corp, state, page],
  );

  const list = useQuery({
    queryKey: ['payment-bank', 'list', listParams],
    queryFn: () => paymentBankApi.list(listParams),
  });

  function refresh() {
    void qc.invalidateQueries({ queryKey: ['payment-bank', 'list'] });
  }

  const corpOptions = meta.data?.filters.find((f) => f.key === 'corp')?.options ?? [
    { value: 'bnncompany', label: 'bnncompany' },
  ];

  const rows: Row[] = list.data?.items ?? [];

  function toggleOne(seq: number) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(seq)) next.delete(seq);
      else next.add(seq);
      return next;
    });
  }

  function toggleAll() {
    setChecked((prev) => (prev.size === rows.length ? new Set() : new Set(rows.map((r) => Number(r.seq)))));
  }

  /** 원본 allCheck_re: 보고서 미등록건만 골라 체크를 뒤집는다. */
  function toggleUnregistered() {
    setChecked((prev) => {
      const next = new Set(prev);
      for (const r of rows) {
        if (!isZeroDate(r.report_date)) continue;
        const seq = Number(r.seq);
        if (next.has(seq)) next.delete(seq);
        else next.add(seq);
      }
      return next;
    });
  }

  const remove = useMutation({
    mutationFn: (seq: number) => paymentBankApi.remove(seq),
    onSuccess: () => {
      setConfirmingDelete(null);
      refresh();
    },
  });

  return (
    <section className="payment-bank-page">
      <header className="page-head">
        <div>
          <h1 className="page-title">
            입금건 관리
            <span className="badge badge--danger">관리자 전용</span>
          </h1>
          <p className="page-desc">무통장 입금건과 세금계산서 발행 상태. (원본 payment/bank_list.php)</p>
        </div>
        <div className="page-head__actions">
          <Link className="btn btn--ghost btn--sm" to="/reports/payment-card">
            카드결제 관리
          </Link>
          <Link className="btn btn--ghost btn--sm" to="/reports/payment-recurring">
            정기결제 관리
          </Link>
        </div>
      </header>

      <div className="filters">
        <Field label="법인">
          <select className="input" value={corp} onChange={(e) => update({ corp: e.target.value })}>
            {corpOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="검색">
          <SearchInput value={q} onChange={setQ} placeholder="입금자명 / 메모 / 금액" />
        </Field>
        <button
          type="button"
          className="btn btn--ghost filters__reset"
          onClick={() => setParams(new URLSearchParams())}
        >
          초기화
        </button>
      </div>

      <div className="tab-group">
        {STATE_TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            className={state === t.value ? 'tab-btn tab-btn--active' : 'tab-btn'}
            onClick={() => update({ state: t.value })}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="toolbar">
        <button type="button" className="btn btn--ghost btn--sm" onClick={toggleUnregistered}>
          보고서 미등록건 선택
        </button>
        <a
          className="btn btn--ghost btn--sm"
          href={checked.size > 0 ? paymentBankApi.transferFileUrl([...checked]) : undefined}
          onClick={(e) => {
            if (checked.size === 0) {
              e.preventDefault();
              window.alert('선택된 입금건이 없습니다.');
            }
          }}
        >
          계좌이체양식 다운로드 ({checked.size})
        </a>
        <Link className="btn btn--ghost btn--sm" to="/payment/bank/check">
          입금엑셀 확인
        </Link>
        <Link className="btn btn--ghost btn--sm" to="/payment/bank/income-check">
          국민은행 중복 확인
        </Link>
        <Link className="btn btn--ghost btn--sm" to="/payment/bank/excel-check">
          엑셀 오류 체크
        </Link>
        <button
          type="button"
          className="btn btn--primary btn--sm"
          style={{ marginLeft: 'auto' }}
          onClick={() => setFormModal({ mode: 'create', row: null })}
        >
          + 입금건 추가
        </button>
      </div>

      {list.isPending ? (
        <Spinner />
      ) : list.isError ? (
        <ErrorBox error={list.error} onRetry={() => void list.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyBox />
      ) : (
        <>
          <div className="table-wrap payment-bank-table-wrap">
            <table className="table payment-bank-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={checked.size > 0 && checked.size === rows.length}
                      onChange={toggleAll}
                    />
                  </th>
                  <th>No</th>
                  <th>은행 / 계좌번호</th>
                  <th>예금주</th>
                  <th style={{ textAlign: 'right' }}>금액</th>
                  <th>입금사유</th>
                  <th>등록자</th>
                  <th>계산서 상태</th>
                  <th>보고서 등록일</th>
                  <th>입금완료일</th>
                  <th>등록일</th>
                  <th>메모</th>
                  <th className="payment-bank-table__manage-head">관리</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  const seq = Number(row.seq);
                  const noTax = String(row.no_tax ?? '0');
                  const taxBadge = TAX_BADGE[noTax];
                  return (
                    <tr key={seq}>
                      <td>
                        <input type="checkbox" checked={checked.has(seq)} onChange={() => toggleOne(seq)} />
                      </td>
                      <td className="td-muted">{(list.data?.total ?? 0) - ((page - 1) * 100 + i)}</td>
                      <td>
                        <div className="product-cell">
                          {row.imageurl ? (
                            <img className="thumb thumb--bank" src={`${CDN_BANK_ICON}${row.imageurl}`} alt="" />
                          ) : null}
                          <div>
                            <div>{text(row.bankname as string)}</div>
                            <span className="td-sub">{text(row.banksn as string)}</span>
                          </div>
                        </div>
                      </td>
                      <td>{text(row.bankinname as string)}</td>
                      <td className="td-right td-amount">{money(row.amount as number)}</td>
                      <td className="td-clip">{text(row.bankmemo as string)}</td>
                      <td>{text(row.up_name as string)}</td>
                      <td>
                        {taxBadge && (
                          <span className={taxBadge.className} title={taxBadge.label}>
                            {taxBadge.shortLabel}
                          </span>
                        )}
                        {!isZeroDate(row.tax_date) ? (
                          <div className="td-sub">
                            {dateOnly(row.tax_date as string)}
                            <br />
                            {text(row.tax_name as string)}
                          </div>
                        ) : noTax !== '0' ? (
                          <StatusButton
                            seq={seq}
                            field="tax_date"
                            withName
                            currentName={row.tax_name as string}
                            onSaved={refresh}
                          />
                        ) : null}
                      </td>
                      <td>
                        {!isZeroDate(row.report_date) ? (
                          <div className="td-sub">
                            {dateOnly(row.report_date as string)}
                            <br />
                            {text(row.re_name as string)}
                          </div>
                        ) : (
                          <StatusButton
                            seq={seq}
                            field="report_date"
                            withName
                            currentName={row.re_name as string}
                            onSaved={refresh}
                          />
                        )}
                      </td>
                      <td>
                        {!isZeroDate(row.succ_date) ? (
                          <span style={{ color: 'var(--ok)' }}>{dateOnly(row.succ_date as string)}</span>
                        ) : (
                          <StatusButton seq={seq} field="succ_date" withName={false} onSaved={refresh} />
                        )}
                      </td>
                      <td className="td-muted">{dateOnly(row.regdate as string)}</td>
                      <td>
                        <div className="payment-bank-table__memo">{text(row.memo as string)}</div>
                      </td>
                      <td className="payment-bank-table__manage-cell">
                        <div className="payment-bank-actions" aria-label="입금건 관리">
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm payment-bank-actions__button"
                            onClick={() => setFormModal({ mode: 'edit', row })}
                          >
                            수정
                          </button>
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm payment-bank-actions__button payment-bank-actions__button--copy"
                            title="이 건의 은행·계좌번호·예금주·입금액·계산서 발행 여부·입금 사유·비고를 그대로 채운 새 입금건을 만듭니다."
                            onClick={() => setFormModal({ mode: 'copy', row })}
                          >
                            복사
                          </button>
                          {confirmingDelete === seq ? (
                            <>
                              <button
                                type="button"
                                className="btn btn--sm payment-bank-actions__button payment-bank-actions__button--delete-confirm"
                                disabled={remove.isPending}
                                onClick={() => remove.mutate(seq)}
                              >
                                정말 삭제
                              </button>
                              <button
                                type="button"
                                className="btn btn--ghost btn--sm payment-bank-actions__button"
                                onClick={() => setConfirmingDelete(null)}
                              >
                                취소
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              className="btn btn--ghost btn--sm payment-bank-actions__button payment-bank-actions__button--delete"
                              onClick={() => setConfirmingDelete(seq)}
                            >
                              삭제
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {list.data?.totals && (
                  <tr className="row--total">
                    <td colSpan={4} style={{ textAlign: 'right' }}>
                      합계
                    </td>
                    <td className="td-right td-amount">{money(list.data.totals.amount)}</td>
                    <td colSpan={8} />
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            page={list.data?.page ?? 1}
            totalPages={list.data?.totalPages ?? 1}
            total={list.data?.total ?? 0}
            onChange={(p) => update({ page: p }, false)}
          />
        </>
      )}

      {formModal && (
        <BankFormModal
          mode={formModal.mode}
          initial={formModal.row}
          corp={corp}
          onClose={() => setFormModal(null)}
          onSaved={() => {
            setFormModal(null);
            refresh();
          }}
        />
      )}
    </section>
  );
}
